// API-Football competition IDs (season values are the season-start year per API-Football convention).
// World Cup 2026 ID is TBC pending dashboard confirmation — see AGENTS.md §14.
export const COMPETITIONS = {
  WORLD_CUP_2026: { id: 1, season: 2026, shortName: 'WC26', isWorldCup: true },
  PREMIER_LEAGUE: { id: 39, season: 2025, shortName: 'EPL', isWorldCup: false },
  CHAMPIONSHIP: { id: 40, season: 2025, shortName: 'CHA', isWorldCup: false },
  LA_LIGA: { id: 140, season: 2025, shortName: 'LAL', isWorldCup: false },
  SERIE_A: { id: 135, season: 2025, shortName: 'SEA', isWorldCup: false },
  BUNDESLIGA: { id: 78, season: 2025, shortName: 'BUN', isWorldCup: false },
  LIGUE_1: { id: 61, season: 2025, shortName: 'LI1', isWorldCup: false },
  UCL: { id: 2, season: 2025, shortName: 'UCL', isWorldCup: false },
  UEL: { id: 3, season: 2025, shortName: 'UEL', isWorldCup: false },
  MLS: { id: 253, season: 2026, shortName: 'MLS', isWorldCup: false },
  ELITESERIEN: { id: 103, season: 2026, shortName: 'NOR', isWorldCup: false },
  BRAZILIAN_SERIE_A: { id: 71, season: 2026, shortName: 'BSA', isWorldCup: false },
  INTL_FRIENDLIES: { id: 10, season: 2026, shortName: 'FRI', isWorldCup: false },
} as const;

// Display ordering for fixtures. Lower number = higher priority (rendered first).
// Any competition not in this map gets the DEFAULT_COMPETITION_PRIORITY, so it
// shows below the curated leagues but above totally unknown ones.
export const COMPETITION_PRIORITY: Record<number, number> = {
  [/* WC 2026         */ 1]:  0,
  [/* UCL             */ 2]: 10,
  [/* EPL             */ 39]: 20,
  [/* La Liga         */ 140]: 30,
  [/* UEL             */ 3]: 40,
  [/* Bundesliga      */ 78]: 50,
  [/* Serie A         */ 135]: 60,
  [/* Ligue 1         */ 61]: 70,
  [/* Brazilian Sér.A */ 71]: 80,
  [/* MLS             */ 253]: 90,
  [/* EFL Champ.      */ 40]: 100,
  [/* Eliteserien     */ 103]: 110,
  [/* Intl friendlies */ 10]: 120,
};
export const DEFAULT_COMPETITION_PRIORITY = 500;

export const PRODUCT_CODES = {
  BRACKET_PREMIUM: 'BRACKET_PREMIUM',
  BOOST: 'BOOST',
  INSURANCE: 'INSURANCE',
  STREAK_PROTECTOR: 'STREAK_PROTECTOR',
  TIPSTER_10: 'TIPSTER_10',
  TIPSTER_25: 'TIPSTER_25',
  RE_LOCK: 'RE_LOCK',
  PREMIUM_SUBSCRIPTION: 'PREMIUM_SUBSCRIPTION',
  PAID_LEAGUE: 'PAID_LEAGUE',
} as const;

/** Telegram Stars subscription period — currently the API only accepts 30d. */
export const PREMIUM_PERIOD_SECONDS = 2_592_000;
export const FREE_TIER_PRIVATE_LEAGUE_CAP = 3;

export type ProductCode = (typeof PRODUCT_CODES)[keyof typeof PRODUCT_CODES];

export const STARS_PRICING: Record<ProductCode, number> = {
  BRACKET_PREMIUM: 300, // 300 early-bird (now 13 May 2026 < 1 Jun), 400 after; see code path on the API/UI side
  BOOST: 75,
  INSURANCE: 90,
  STREAK_PROTECTOR: 60,
  TIPSTER_10: 600,
  TIPSTER_25: 1400,
  RE_LOCK: 150,
  PREMIUM_SUBSCRIPTION: 200, // per 30-day period
  PAID_LEAGUE: 2000, // one-shot to convert a private league into a branded sponsored league
};

export const INSURANCE_PAYOUT_POINTS = 7; // 50% of expected value, rounded down

export const SCORING = {
  LEAGUE: {
    ONE_X_TWO_FAV: 3,
    ONE_X_TWO_UNDERDOG: 5,
    BTTS: 2,
    CORRECT_SCORE: 10,
    FAVOURITE_PROB_THRESHOLD: 0.4,
  },
  KNOCKOUT: {
    WINNER: 5,
    METHOD: 3,
    SCORE_AT_90: 8,
    GOALSCORER: 5,
  },
  BRACKET: {
    GROUP_WINNER: 3,
    GROUP_RUNNER_UP: 2,
    GROUP_THIRD: 1,
    R32: 1,
    R16: 3,
    QF: 5,
    SF: 8,
    FINALIST: 15,
    CHAMPION: 30,
    GOLDEN_BOOT: 25,
  },
  MODIFIERS: {
    STREAK_3_BONUS: 1,
    STREAK_7_BONUS: 2,
    BOOST_MULTIPLIER: 2,
    STREAK_3_THRESHOLD: 3,
    STREAK_7_THRESHOLD: 7,
  },
} as const;

export const WC_2026 = {
  TOTAL_TEAMS: 48,
  TOTAL_GROUPS: 12,
  TEAMS_PER_GROUP: 4,
  GROUP_LABELS: ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'] as const,
  AUTO_ADVANCERS: 24, // top 2 from each of 12 groups
  BEST_THIRDS: 8,
  R32_TEAMS: 32,
  BRACKET_LOCK_DATE: '2026-06-10T23:59:00Z',
  TOURNAMENT_START: '2026-06-11T00:00:00Z',
  TOURNAMENT_END: '2026-07-19T23:59:00Z',
} as const;

export const KO_STAGES = ['R32', 'R16', 'QF', 'SF', 'FINAL'] as const;
export type KoStage = (typeof KO_STAGES)[number];

export const KO_SLOT_COUNTS: Record<KoStage, number> = {
  R32: 16,
  R16: 8,
  QF: 4,
  SF: 2,
  FINAL: 1,
};
